import {
  CollectorInput,
  CollectorOutputReadable,
  DeviceInput,
  DeviceOutput,
  SiteConfigurationInput,
  SiteConfigurationOutputReadable,
} from "../../../../services/client";

type AnyCollector = CollectorInput | CollectorOutputReadable;
type AnyDevice = DeviceInput | DeviceOutput;

export type FlatCollector = Omit<AnyCollector, "children"> & {
  parent: string | null;
  child_names: string[];
};

export type FieldChange = {
  field: string;
  oldValue: unknown;
  newValue: unknown;
};

export type DeviceDiffEntry =
  | { kind: "added"; name: string; device: AnyDevice; fields: FieldChange[] }
  | { kind: "removed"; name: string; device: AnyDevice; fields: FieldChange[] }
  | {
      kind: "changed";
      name: string;
      oldDevice: AnyDevice;
      newDevice: AnyDevice;
      changes: FieldChange[];
    };

export type CollectorDiffEntry =
  | {
      kind: "added";
      name: string;
      collector: FlatCollector;
      fields: FieldChange[];
    }
  | {
      kind: "removed";
      name: string;
      collector: FlatCollector;
      fields: FieldChange[];
    }
  | {
      kind: "changed";
      name: string;
      oldCollector: FlatCollector;
      newCollector: FlatCollector;
      changes: FieldChange[];
    };

export type SiteConfigDiff = {
  gridConnection: FieldChange[];
  devices: DeviceDiffEntry[];
  collectors: CollectorDiffEntry[];
};

function flattenCollectors(
  collectors: AnyCollector[] | null | undefined,
  parent: string | null = null,
  out: Map<string, FlatCollector> = new Map(),
): Map<string, FlatCollector> {
  for (const c of collectors ?? []) {
    const children = c.children ?? [];
    const rest: Record<string, unknown> = { ...(c as Record<string, unknown>) };
    delete rest.children;
    out.set(c.name, {
      ...(rest as unknown as Omit<AnyCollector, "children">),
      parent,
      child_names: children.map((child) => child.name),
    });
    flattenCollectors(children, c.name, out);
  }
  return out;
}

function indexDevices(devices: AnyDevice[]): Map<string, AnyDevice> {
  const out = new Map<string, AnyDevice>();
  for (const d of devices) {
    out.set(d.name, d);
  }
  return out;
}

function deepEqual(a: unknown, b: unknown): boolean {
  if (a === b) {
    return true;
  }
  if (a === null || b === null) {
    return a === b;
  }
  if (typeof a !== typeof b) {
    return false;
  }
  if (typeof a !== "object") {
    return false;
  }
  if (Array.isArray(a) !== Array.isArray(b)) {
    return false;
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }
    for (let i = 0; i < a.length; i++) {
      if (!deepEqual(a[i], b[i])) {
        return false;
      }
    }
    return true;
  }
  const ao = a as Record<string, unknown>;
  const bo = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(ao), ...Object.keys(bo)]);
  for (const k of keys) {
    if (!deepEqual(ao[k], bo[k])) {
      return false;
    }
  }
  return true;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function isAbsent(value: unknown): boolean {
  return value === undefined || value === null;
}

function hasObjectItems(value: unknown): boolean {
  return Array.isArray(value) && value.some(isPlainObject);
}

function asRecord(value: unknown): Record<string, unknown> {
  if (Array.isArray(value)) {
    return Object.fromEntries(value.map((item, i) => [String(i), item]));
  }
  return isPlainObject(value) ? value : {};
}

function diffValues(
  path: string,
  oldValue: unknown,
  newValue: unknown,
  out: FieldChange[],
): void {
  if (deepEqual(oldValue, newValue)) {
    return;
  }
  const bothObjects =
    (isPlainObject(oldValue) || isAbsent(oldValue)) &&
    (isPlainObject(newValue) || isAbsent(newValue));
  const bothArrays =
    (Array.isArray(oldValue) || isAbsent(oldValue)) &&
    (Array.isArray(newValue) || isAbsent(newValue)) &&
    (hasObjectItems(oldValue) || hasObjectItems(newValue));
  if (
    bothArrays ||
    (bothObjects && !(isAbsent(oldValue) && isAbsent(newValue)))
  ) {
    const before = out.length;
    const oldRecord = asRecord(oldValue);
    const newRecord = asRecord(newValue);
    const keys = new Set([
      ...Object.keys(oldRecord),
      ...Object.keys(newRecord),
    ]);
    for (const k of keys) {
      const childPath = bothArrays
        ? `${path}[${k}]`
        : path
          ? `${path}.${k}`
          : k;
      diffValues(childPath, oldRecord[k], newRecord[k], out);
    }
    if (out.length > before) {
      return;
    }
  }
  out.push({ field: path, oldValue, newValue });
}

function diffFields(
  oldObj: Record<string, unknown>,
  newObj: Record<string, unknown>,
): FieldChange[] {
  const changes: FieldChange[] = [];
  diffValues("", oldObj, newObj, changes);
  return changes;
}

function isUnset(value: unknown): boolean {
  if (isAbsent(value) || value === "") {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return isPlainObject(value) && Object.keys(value).length === 0;
}

function setFields(oldObj: object, newObj: object): FieldChange[] {
  return diffFields(
    oldObj as Record<string, unknown>,
    newObj as Record<string, unknown>,
  ).filter(
    (c) => c.field !== "name" && (!isUnset(c.oldValue) || !isUnset(c.newValue)),
  );
}

export function computeSiteConfigDiff(
  serverConfig:
    | SiteConfigurationInput
    | SiteConfigurationOutputReadable
    | null
    | undefined,
  draftConfig:
    | SiteConfigurationInput
    | SiteConfigurationOutputReadable
    | null
    | undefined,
): SiteConfigDiff {
  const serverDevices = indexDevices(serverConfig?.device_list ?? []);
  const draftDevices = indexDevices(draftConfig?.device_list ?? []);
  const serverCollectors = flattenCollectors(serverConfig?.collector_list);
  const draftCollectors = flattenCollectors(draftConfig?.collector_list);

  const devices: DeviceDiffEntry[] = [];
  for (const [name, device] of draftDevices) {
    const old = serverDevices.get(name);
    if (!old) {
      devices.push({
        kind: "added",
        name,
        device,
        fields: setFields({}, device),
      });
    } else if (!deepEqual(old, device)) {
      devices.push({
        kind: "changed",
        name,
        oldDevice: old,
        newDevice: device,
        changes: diffFields(
          old as Record<string, unknown>,
          device as Record<string, unknown>,
        ),
      });
    }
  }
  for (const [name, device] of serverDevices) {
    if (!draftDevices.has(name)) {
      devices.push({
        kind: "removed",
        name,
        device,
        fields: setFields(device, {}),
      });
    }
  }

  const collectors: CollectorDiffEntry[] = [];
  for (const [name, collector] of draftCollectors) {
    const old = serverCollectors.get(name);
    if (!old) {
      collectors.push({
        kind: "added",
        name,
        collector,
        fields: setFields({}, collector),
      });
    } else if (!deepEqual(old, collector)) {
      collectors.push({
        kind: "changed",
        name,
        oldCollector: old,
        newCollector: collector,
        changes: diffFields(
          old as unknown as Record<string, unknown>,
          collector as unknown as Record<string, unknown>,
        ),
      });
    }
  }
  for (const [name, collector] of serverCollectors) {
    if (!draftCollectors.has(name)) {
      collectors.push({
        kind: "removed",
        name,
        collector,
        fields: setFields(collector, {}),
      });
    }
  }

  const gridConnection = diffFields(
    serverConfig?.grid_connection ?? {},
    draftConfig?.grid_connection ?? {},
  );

  return { gridConnection, devices, collectors };
}

export function diffChangeCount(diff: SiteConfigDiff): number {
  return (
    (diff.gridConnection.length > 0 ? 1 : 0) +
    diff.devices.length +
    diff.collectors.length
  );
}
